import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppStorage } from '@workspace/ui/hooks/useAppStorage';
import { storageClient } from '@workspace/ui/hooks/useAppStorage';
import type { StorageItem } from '@workspace/ui/hooks/useAppStorage';
import { useAuth } from '@workspace/ui/hooks/useAuth';
import type { Message, ChatSession, MessageAttachment } from '../types';
import { listPendingTurns, mergePendingTurns, deletePendingTurn } from './pending-turns';

// Storage keys are UNVERSIONED and must stay that way, despite the usual
// advice to suffix them (`:v1`). These keys hold the only copy of an
// unauthenticated user's chat history — renaming one doesn't migrate that
// history, it orphans it, which is the failure this file's merge logic exists
// to prevent. Evolve the SHAPE behind these keys instead — readers already
// tolerate unknown/missing fields (see the `?? default` reads for the flags
// added by tdd-0042). A breaking change that genuinely cannot be read
// leniently needs a versioned field on ChatSession plus a migration on load,
// NOT a new key. See tdd-0074.
const SESSIONS_STORAGE_KEY = 'ai-assist-sessions';
const ACTIVE_SESSION_KEY = 'ai-assist-active-session';
const MIGRATED_KEY = 'ai-assist:migrated-to-remote';
// Private ("incognito") sessions live in sessionStorage: they survive a page
// refresh but are wiped by the browser on tab/window close. Never localStorage,
// never remote/DB (tdd-0042).
const PRIVATE_SESSIONS_KEY = 'ai-assist-private-sessions';
const APP_NAME = 'chat';

interface UseChatSessionsOptions {
  enabled?: boolean;
}

function generateTitle(messages: Message[]): string {
  const firstUserMessage = messages.find((m) => m.role === 'user');
  if (firstUserMessage?.content) {
    const content = firstUserMessage.content.trim();
    return content.length > 40 ? content.slice(0, 40) + '...' : content;
  }
  return 'New Chat';
}

/**
 * Union two session lists by id, newest copy winning, sorted newest-first.
 *
 * The load-bearing rule is that a session present in `local` but absent from
 * `remote` is KEPT. Absence from the remote list is not evidence of deletion —
 * it can equally mean a fetch failed, or the body had not finished uploading
 * when the index was written. Treating it as deletion is what destroyed history.
 *
 * Known trade-off: a session deleted on another device reappears here until the
 * next authoritative load, because there are no tombstones. That is deliberate —
 * an occasional resurrected thread is recoverable; silent data loss is not.
 */
export function mergeSessions(
  local: ChatSession[],
  remote: ChatSession[],
): ChatSession[] {
  const byId = new Map<string, ChatSession>();
  for (const s of local) byId.set(s.id, s);
  for (const s of remote) {
    const existing = byId.get(s.id);
    // `>=` so an equal timestamp prefers remote — it is the shared source of
    // truth and more likely to reflect another device's edit.
    if (!existing || s.updatedAt >= existing.updatedAt) byId.set(s.id, s);
  }
  return Array.from(byId.values()).sort((a, b) => b.updatedAt - a.updatedAt);
}

// ── Remote storage helpers ──────────────────────────────────────────

interface SessionIndex {
  sessionIds: string[];
  activeSessionId: string | null;
}

function storagePath(key: string): string {
  return `/v1/storage/${APP_NAME}/${encodeURIComponent(key)}`;
}

async function fetchRemoteSession(sessionId: string): Promise<ChatSession | null> {
  try {
    const { data: item } = await storageClient.get<StorageItem<ChatSession>>(
      storagePath(`sessions/${sessionId}.json`),
    );
    return item?.data ?? null;
  } catch {
    return null;
  }
}

async function saveRemoteSession(session: ChatSession): Promise<void> {
  // A migrated session is written under its NEW uuid path, leaving the object
  // at the old `sessions/session-<epoch>.json` behind. That orphan is not
  // harmless: `mergeSessions` treats "present locally, absent remotely" as
  // "keep" (never as a deletion), so on the next device sync the stale body
  // returns as a SECOND copy of the same conversation. Drop it once the new
  // path is durably written — in that order, so a failure here can only ever
  // leave a duplicate, never lose the only copy.
  const staleId = session.migratedFrom;
  const toWrite = staleId ? { ...session, migratedFrom: undefined } : session;
  await storageClient.put(storagePath(`sessions/${session.id}.json`), { data: toWrite });
  if (staleId) {
    await deleteRemoteSession(staleId).catch(() => {
      // Best-effort. The old body reappearing as a duplicate is annoying;
      // failing the save because cleanup failed would be worse.
    });
  }
}

async function deleteRemoteSession(sessionId: string): Promise<void> {
  await storageClient.delete(storagePath(`sessions/${sessionId}.json`));
}

// ── session id migration (uuid) ──────────────────────────────────────

/**
 * Sessions created before the URL work used `session-<epoch-ms>` ids. Those ids
 * are now part of a shareable address (`/conversation/<id>`), so new sessions
 * mint a real UUID instead — a timestamp in a URL is both guessable and
 * meaningless to a reader.
 *
 * MIGRATION, NOT A KEY CHANGE
 *
 * This file's header warns that a breaking change needs "a versioned field on
 * ChatSession plus a migration on load, NOT a new key" — which is exactly the
 * shape here. The storage keys are untouched; only the `id` FIELD is rewritten,
 * at the load boundary, so every reader downstream sees UUIDs and no caller
 * needs to know two formats exist.
 *
 * `migratedFrom` is what keeps the remote side honest. Remote bodies are stored
 * at `sessions/<id>.json`, so a renamed session writes to a NEW path and the old
 * body is left behind — and because `mergeSessions` deliberately treats "absent
 * from remote" as "keep, might be a failed fetch", that orphan would come back
 * as a duplicate on the next device sync. Recording the old id lets the sync
 * delete the stale body exactly once.
 */
const LEGACY_SESSION_ID = /^session-\d+$/;

function newSessionId(): string {
  // `crypto.randomUUID` needs a secure context. Every real deployment is HTTPS
  // (or localhost, which counts), but the widget also ships as a web component
  // onto pages we do not control, so the fallback keeps id generation working
  // rather than throwing inside someone else's page.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * Rewrite legacy ids to UUIDs, in place, preserving everything else.
 *
 * Idempotent: a session already carrying a UUID is returned untouched, so this
 * is safe to run on every load. Returns the sessions plus the id map, which the
 * caller needs to re-point the active-session pointer at the same conversation
 * (otherwise migrating silently drops the user back to a different thread).
 */
export function migrateSessionIds(sessions: ChatSession[]): {
  sessions: ChatSession[];
  idMap: Map<string, string>;
} {
  const idMap = new Map<string, string>();
  const migrated = sessions.map((s) => {
    if (typeof s.id !== 'string' || !LEGACY_SESSION_ID.test(s.id)) return s;
    const next = newSessionId();
    idMap.set(s.id, next);
    return { ...s, id: next, migratedFrom: s.id };
  });
  return { sessions: migrated, idMap };
}

// ── localStorage helpers ────────────────────────────────────────────

function localLoadSessions(): ChatSession[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SESSIONS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Legacy `session-<epoch>` ids become UUIDs here, at the single load
    // boundary, so nothing downstream has to know two formats ever existed.
    // Idempotent, so it costs one regex per session on an already-migrated
    // store. The write-back below is what makes it stick — without it the
    // migration would re-run every load and mint a NEW id each time, so a
    // shared `/conversation/<id>` link would rot on the next refresh.
    const { sessions, idMap } = migrateSessionIds(parsed);
    if (idMap.size > 0) {
      const activeId = localGetActiveId();
      // Written directly rather than via localSaveSessions: that function's
      // empty-list guard calls back into THIS function, and a loader that can
      // re-enter itself is a trap waiting for the next edit. The guard has
      // nothing to add here anyway — a migration never produces fewer sessions
      // than it read, so the case it protects against cannot arise.
      try {
        localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(sessions));
      } catch (err) {
        // Non-fatal: the in-memory ids are already migrated, so this session
        // works. It just migrates again (to different uuids) on next load.
        console.error('[chat-sessions] Failed to persist migrated ids:', err);
      }
      // Follow the pointer to the same conversation. Skipped and the user
      // silently lands in a different thread after the upgrade.
      if (activeId && idMap.has(activeId)) {
        localSetActiveId(idMap.get(activeId)!);
      }
      console.info(`[chat-sessions] Migrated ${idMap.size} session id(s) to uuid.`);
    }
    return sessions;
  } catch { return []; }
}

/**
 * Drop attachment bytes that are already in durable storage.
 *
 * `dataUrl` is base64 — roughly 1.37x the file's real size — and a session is
 * persisted as one JSON blob, so a couple of photos can approach the ~5MB
 * localStorage budget on their own. Once an attachment has a `storageKey` the
 * bytes are in the user's artifact store, and the copy in session JSON is pure
 * quota cost.
 *
 * A quota failure here is not loud: the write is caught and logged, React state
 * moves on, and the next reload silently hydrates whatever last fit. Keeping
 * the blob small is what stops that happening at all.
 *
 * Two conditions, both required.
 *
 * There must be a `storageKey`: without one the data URL is the only copy — a
 * private session, an unauthenticated user, or a persist that failed — and
 * dropping it would lose the attachment entirely.
 *
 * And it must NOT be an image. Images render their preview straight from
 * `dataUrl` in the transcript, so stripping one shows a broken thumbnail on
 * every reload; serving them from storage instead means an async fetch per
 * attachment in the renderer, which is a bigger change than this. Non-images
 * render as a file card that never reads the bytes, so for them the stored
 * copy is pure cost — and documents are the large ones anyway.
 */
function stripPersistedAttachmentBytes(sessions: ChatSession[]): ChatSession[] {
  let changed = false;

  const next = sessions.map((session) => {
    if (!session.messages?.length) return session;

    let sessionChanged = false;
    const messages = session.messages.map((message) => {
      if (!message.attachments?.length) return message;
      const strippable = (a: MessageAttachment) =>
        Boolean(a.storageKey) && Boolean(a.dataUrl) && !a.isImage;
      if (!message.attachments.some(strippable)) return message;

      sessionChanged = true;
      return {
        ...message,
        attachments: message.attachments.map((a) =>
          strippable(a) ? { ...a, dataUrl: '' } : a,
        ),
      };
    });

    if (!sessionChanged) return session;
    changed = true;
    return { ...session, messages };
  });

  // Identity preserved when nothing was stripped, so this stays cheap on the
  // common path and never invalidates memoised consumers for no reason.
  return changed ? next : sessions;
}

/**
 * Persist the session list, refusing to replace real history with nothing.
 *
 * An empty write is almost never legitimate. It IS legitimate when the user
 * deletes their last session, or when every session in state is private (those
 * never touch durable storage) — both pass `allowEmpty`. Everything else that
 * computes `[]` is a bug, and this is the last line of defence before the
 * user's history is gone.
 *
 * This is not hypothetical. Observed 2026-08-05: localStorage was found holding
 * `"[]"` while the active-session pointer still named a session that had just
 * been wiped, and the remote index was `{sessionIds: []}` with the session body
 * still on disk, orphaned. Once the empty value lands, hydration loads `[]`, so
 * the next write persists `[]` again — self-perpetuating. Refusing the write
 * breaks that loop regardless of which caller computed the empty array.
 */
function localSaveSessions(
  sessions: ChatSession[],
  opts: { allowEmpty?: boolean } = {},
): boolean {
  if (typeof window === 'undefined') return false;
  if (sessions.length === 0 && !opts.allowEmpty) {
    const existing = localLoadSessions();
    if (existing.length > 0) {
      console.error(
        `[chat-sessions] Refused to overwrite ${existing.length} stored session(s) ` +
          `with an empty list. This is a bug — the caller computed [] without ` +
          `intending a deletion. History preserved.`,
      );
      // False: the stored list does NOT reflect what the caller asked for, and
      // a caller that gates on this must not move a pointer on the strength of
      // a refused write.
      return false;
    }
  }
  try {
    localStorage.setItem(
      SESSIONS_STORAGE_KEY,
      // Stripped at the boundary, not in state: the running app still shows
      // previews from `dataUrl`, and only the stored copy goes without.
      JSON.stringify(stripPersistedAttachmentBytes(sessions)),
    );
    return true;
  } catch (err) {
    // Was a silent catch. A quota failure leaves storage holding the PREVIOUS
    // value while React state moves on — a divergence worth knowing about.
    console.error('[chat-sessions] Failed to write sessions to localStorage:', err);
    return false;
  }
}

function localGetActiveId(): string | null {
  if (typeof window === 'undefined') return null;
  // The only unguarded read left in this file. localStorage throws rather than
  // returning null in Safari private browsing, and this one runs during
  // hydration — so it took the widget down on mount rather than degrading to
  // "no active session", which is a perfectly serviceable state (tdd-0074).
  try {
    return localStorage.getItem(ACTIVE_SESSION_KEY);
  } catch {
    return null;
  }
}

function localSetActiveId(id: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (id) localStorage.setItem(ACTIVE_SESSION_KEY, id);
    else localStorage.removeItem(ACTIVE_SESSION_KEY);
  } catch { /* ignore */ }
}

// ── sessionStorage helpers (private/incognito sessions) ──────────────
// Survive refresh, wiped on tab close. Stores the private sessions + which one
// was active, so a refresh restores the in-progress private chat.

interface PrivateStore {
  sessions: ChatSession[];
  activeSessionId: string | null;
}

function privateLoad(): PrivateStore {
  if (typeof window === 'undefined') return { sessions: [], activeSessionId: null };
  try {
    const raw = sessionStorage.getItem(PRIVATE_SESSIONS_KEY);
    if (!raw) return { sessions: [], activeSessionId: null };
    const parsed = JSON.parse(raw) as PrivateStore;
    return {
      sessions: Array.isArray(parsed.sessions) ? parsed.sessions : [],
      activeSessionId: parsed.activeSessionId ?? null,
    };
  } catch { return { sessions: [], activeSessionId: null }; }
}

function privateSave(sessions: ChatSession[], activeSessionId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    const priv = sessions.filter((s) => s.isPrivate);
    if (priv.length === 0) {
      sessionStorage.removeItem(PRIVATE_SESSIONS_KEY);
      return;
    }
    const activeIsPrivate = priv.some((s) => s.id === activeSessionId);
    sessionStorage.setItem(
      PRIVATE_SESSIONS_KEY,
      JSON.stringify({ sessions: priv, activeSessionId: activeIsPrivate ? activeSessionId : null }),
    );
  } catch { /* sessionStorage full / unavailable */ }
}

// ── Hook ────────────────────────────────────────────────────────────

export function useChatSessions(options: UseChatSessionsOptions = {}) {
  const { enabled = true } = options;
  const { authenticated } = useAuth();
  const {
    data: remoteIndex,
    loading: remoteIndexLoading,
    save: saveRemoteIndex,
  } = useAppStorage<SessionIndex>(APP_NAME, 'sessions/index.json');

  // Assigned during render, NOT in an effect: an effect-assigned ref is one
  // render behind, and the sync below reads this on the same commit that SWR
  // first populates `data`. A stale read there would skip the restore entirely.
  const remoteIndexRef = useRef<SessionIndex | undefined>(undefined);
  remoteIndexRef.current = remoteIndex;

  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const initialLoadDone = useRef(false);
  const migratingRef = useRef(false);
  /**
   * Set once a remote sync has COMPLETED SUCCESSFULLY with a defined index.
   *
   * Not set on entry: a sync that bailed early (index still undefined) or threw
   * would otherwise mark itself done and disable remote hydration for the whole
   * page lifetime — silent, and worse than the re-runs it was meant to stop.
   *
   * This exists because the effect previously depended on `remoteIndex`, whose
   * identity changes on every save (SWR `mutate()` refetches). Each message sent
   * therefore re-ran the full sync mid-conversation.
   */
  const syncedRef = useRef(false);
  // State mirror of `syncedRef`, because `isSettled` is read during render and
  // a ref flip would not re-render the consumers waiting on it.
  const [remoteSynced, setRemoteSynced] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load sessions on mount (localStorage first for fast hydration)
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setIsHydrated(true);
      return;
    }

    const localSessions = localLoadSessions();
    const localActiveId = localGetActiveId();
    // Private sessions from sessionStorage survive a refresh (wiped on tab close).
    const priv = privateLoad();
    const merged = [...priv.sessions, ...localSessions];
    if (merged.length > 0) {
      setSessions(merged);
    }
    // A live private chat (this tab) takes precedence as the active session.
    const activeId = priv.activeSessionId ?? localActiveId;
    if (activeId) {
      setActiveSessionId(activeId);
    }

    setIsHydrated(true);
    initialLoadDone.current = true;
  }, [enabled]);

  // When authenticated, load from remote and merge/migrate
  useEffect(() => {
    if (!enabled || !authenticated || !isHydrated || migratingRef.current) return;
    if (syncedRef.current) return;
    if (remoteIndexLoading) return;
    if (typeof window === 'undefined') return;

    migratingRef.current = true;
    ;(async () => {
      // Read through the ref (assigned during render) rather than closing over
      // the value, so removing it from the deps above can't serve a stale index.
      const remoteIndex = remoteIndexRef.current;
      try {
        if (remoteIndex && remoteIndex.sessionIds.length > 0) {
          // Load remote sessions
          const remoteFetched: ChatSession[] = [];
          // Turns recovered this pass, cleared only once their merged session
          // has been written back (tdd-0090).
          const pendingToClear: Array<{ id: string; turnIds: string[] }> = [];
          for (const id of remoteIndex.sessionIds) {
            try {
              const session = await fetchRemoteSession(id);
              if (session) {
                // Fold in any turn the server saved because this client was
                // gone when it finished (tdd-0090). Done HERE, before the
                // merge, so a recovered turn reaches `mergeSessions` as part of
                // the remote session rather than as a competing copy of it.
                const pending = await listPendingTurns(id);
                if (pending.length > 0) {
                  session.messages = mergePendingTurns(session.messages, pending);
                  session.updatedAt = Date.now();
                  // Clear only after the merged body is written back below;
                  // deleting first would lose the turn if the save failed.
                  pendingToClear.push({ id, turnIds: pending.map((p) => p.turnId) });
                }
                remoteFetched.push(session);
              }
            } catch { /* skip broken */ }
          }

          // Migrate REMOTE ids too, not just local ones.
          //
          // `localLoadSessions` rewrites a legacy `session-<epoch>` id to a uuid
          // and writes that back — but the remote copy still carried the legacy
          // id, and `mergeSessions` keys purely on `id`. So the same
          // conversation arrived as two: the migrated local one and the legacy
          // remote one. The union kept both, the merged list was saved back,
          // and the legacy session was migrated AGAIN on the next load — a new
          // uuid every time.
          //
          // That is not theoretical: it produced 14 empty duplicates of one
          // conversation and grew the remote index to 100 entries against 19
          // files before it was noticed (2026-08-26).
          //
          // Migrating here means both sides reach `mergeSessions` under the
          // same id, so they collapse into one. `migratedFrom` then lets
          // `saveRemoteSession` drop the stale legacy body exactly once.
          const { sessions: remoteSessions } = migrateSessionIds(remoteFetched);
          const remoteMigrated = remoteSessions.filter((s) => s.migratedFrom);
          for (const session of remoteMigrated) {
            // Writes the new uuid body and deletes the legacy one. Best-effort:
            // a failure here leaves an orphan, which the next hydration will
            // migrate again — annoying, not lossy.
            await saveRemoteSession(session).catch(() => {});
          }
          if (remoteMigrated.length > 0) {
            const staleIds = new Set(remoteMigrated.map((s) => s.migratedFrom!));
            await saveRemoteIndex({
              sessionIds: [
                ...remoteIndex.sessionIds.filter((id) => !staleIds.has(id)),
                ...remoteMigrated.map((s) => s.id),
              ],
              activeSessionId: remoteIndex.activeSessionId,
            }).catch(() => {});
          }

          // Check for local sessions that need migrating
          const hasMigrated = localStorage.getItem(MIGRATED_KEY);
          if (!hasMigrated) {
            const localSessions = localLoadSessions();
            const remoteIds = new Set(remoteIndex.sessionIds);
            const toMigrate = localSessions.filter((s) => !remoteIds.has(s.id));
            for (const session of toMigrate) {
              await saveRemoteSession(session);
              remoteSessions.push(session);
            }
            if (toMigrate.length > 0) {
              await saveRemoteIndex({
                sessionIds: [...remoteIndex.sessionIds, ...toMigrate.map((s) => s.id)],
                activeSessionId: remoteIndex.activeSessionId,
              });
            }
            localStorage.setItem(MIGRATED_KEY, '1');
          }

          // Persist the recovered turns, then drop the sidecars.
          //
          // In THAT order: a delete before a durable write would lose the turn
          // outright if the save failed, whereas a delete that fails just
          // leaves a sidecar the next merge folds in again to the same result
          // (merging is idempotent on message id).
          for (const { id, turnIds } of pendingToClear) {
            const merged = remoteFetched.find((s) => s.id === id);
            if (!merged) continue;
            try {
              await saveRemoteSession(merged);
              await Promise.all(turnIds.map((tid) => deletePendingTurn(id, tid)));
            } catch {
              /* keep the sidecars; they will be merged again next load */
            }
          }

          // MERGE, never replace. A remote list that came back short — because a
          // fetch failed, or because a session was indexed before its body
          // finished uploading — must not be able to delete local history. The
          // union keeps whichever copy is newer and never drops a local-only
          // session.
          setSessions((prev) => {
            const privateSessions = prev.filter((s) => s.isPrivate);
            const localNormal = prev.filter((s) => !s.isPrivate);
            return [...privateSessions, ...mergeSessions(localNormal, remoteSessions)];
          });
          // Don't override the active session if a private chat is currently active.
          setActiveSessionId((prevActive) => {
            const activeIsPrivate = privateLoad().sessions.some((s) => s.id === prevActive);
            if (activeIsPrivate) return prevActive;
            return remoteIndex.activeSessionId ?? prevActive;
          });
          // Sync to localStorage for fast next load (normal sessions only).
          // Must be the MERGED list, not the raw remote one — writing the raw
          // list here would undo the merge above on the next hydration.
          const wrote = localSaveSessions(
            mergeSessions(localLoadSessions(), remoteSessions),
          );
          // The pointer moves ONLY if the list it points into actually landed.
          //
          // These two writes are wildly different sizes — the list can blow the
          // origin's quota while a 40-byte id sails through — so writing the
          // pointer unconditionally left localStorage naming an active session
          // it did not contain. On reload that resolves to nothing: no thread
          // renders and the app looks like it started a new conversation.
          // Observed exactly this, with the active id absent from all six
          // stored sessions.
          if (wrote && remoteIndex.activeSessionId) {
            localSetActiveId(remoteIndex.activeSessionId);
          }
        } else if (!localStorage.getItem(MIGRATED_KEY)) {
          // No remote data — migrate all local sessions up
          const localSessions = localLoadSessions();
          if (localSessions.length > 0) {
            for (const session of localSessions) {
              await saveRemoteSession(session);
            }
            await saveRemoteIndex({
              sessionIds: localSessions.map((s) => s.id),
              activeSessionId: localGetActiveId(),
            });
          }
          localStorage.setItem(MIGRATED_KEY, '1');
        }
        // Reached only when the sync ran to completion. Set here, not on entry:
        // an early bail or a throw must leave it unset so a later render can
        // retry, rather than permanently disabling remote hydration.
        syncedRef.current = true;
        setRemoteSynced(true);
      } catch (err) {
        console.error('Failed to sync sessions with remote:', err);
      } finally {
        migratingRef.current = false;
      }
    })();
    // `remoteIndex` is deliberately NOT a dependency — its identity changes on
    // every save (SWR mutate refetches), which re-ran this whole sync after each
    // message. It is read from `remoteIndexRef` instead, assigned during render.
  }, [enabled, authenticated, isHydrated, remoteIndexLoading, saveRemoteIndex]);

  // Debounced remote persist helper
  const persistRemote = useCallback(
    (
      newSessions: ChatSession[],
      newActiveId: string | null,
      allowEmpty = false,
    ) => {
      if (!authenticated) return;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          // Save index — with the same refuse-to-empty guard as localStorage.
          // This write is the more destructive of the two: emptying the index
          // orphans every session body (they remain in storage but nothing
          // references them), and on next load the sync sees zero ids, skips
          // its restore branch, and the history is unreachable. Exactly what
          // happened on 2026-08-05.
          const nextIds = newSessions.map((s) => s.id);
          const currentIds = remoteIndexRef.current?.sessionIds ?? [];
          if (nextIds.length === 0 && currentIds.length > 0 && !allowEmpty) {
            console.error(
              `[chat-sessions] Refused to empty the remote session index ` +
                `(${currentIds.length} session(s) would have been orphaned).`,
            );
          } else {
            await saveRemoteIndex({
              sessionIds: nextIds,
              activeSessionId: newActiveId,
            });
          }
          // Save only sessions that were recently updated (within last 2 seconds)
          const cutoff = Date.now() - 2000;
          for (const session of newSessions) {
            if (session.updatedAt >= cutoff) {
              await saveRemoteSession(session);
            }
          }
        } catch (err) {
          console.error('Failed to persist sessions to remote:', err);
        }
      }, 1000);
    },
    [authenticated, saveRemoteIndex],
  );

  // Cleanup timeout
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // Persist sessions to localStorage + debounced remote. Private ("incognito")
  // sessions are stripped here so they never touch any store — they live only in
  // React state and vanish on tab close (tdd-0042). The active id is likewise not
  // persisted when it points at a private session.
  const persistSessions = useCallback(
    (
      newSessions: ChatSession[],
      newActiveId?: string | null,
      /**
       * The caller genuinely intends an empty list — the user deleted their
       * last session. Without this, the guards below preserve history instead,
       * because every other route to `[]` observed so far has been a bug.
       */
      opts: { allowEmpty?: boolean } = {},
    ) => {
      if (!enabled || typeof window === 'undefined') return false;
      const wantedActiveId = newActiveId ?? activeSessionId;
      // Normal sessions → localStorage + remote. Active id is dropped when it
      // points at a private session (those never touch the durable stores).
      const persistable = newSessions.filter((s) => !s.isPrivate);
      const activeIsPrivate = newSessions.some((s) => s.id === wantedActiveId && s.isPrivate);
      // An all-private array legitimately persists nothing: those sessions are
      // sessionStorage-only by design, so `[]` here is not data loss.
      const allPrivate = newSessions.length > 0 && persistable.length === 0;
      const allowEmpty = opts.allowEmpty === true || allPrivate;
      const wrote = localSaveSessions(persistable, { allowEmpty });
      persistRemote(persistable, activeIsPrivate ? null : wantedActiveId, allowEmpty);
      // Private sessions → sessionStorage only (survive refresh, die on tab close).
      privateSave(newSessions, wantedActiveId);
      return wrote;
    },
    [enabled, persistRemote, activeSessionId]
  );

  // Persist active session ID
  const persistActiveSession = useCallback(
    (sessionId: string | null) => {
      if (!enabled || typeof window === 'undefined') return;
      localSetActiveId(sessionId);
      // Remote index update happens via persistSessions
    },
    [enabled]
  );

  // Create a new session. `opts.isPrivate` starts an incognito thread (brain
  // hard-off, never persisted); `opts.memoryEnabled` seeds the per-thread brain
  // flag for a normal thread (defaults handled by the caller / ChatWidget).
  const createSession = useCallback(
    (messages: Message[] = [], opts: { isPrivate?: boolean; memoryEnabled?: boolean } = {}): ChatSession => {
      const now = Date.now();
      // Set inside the updater below, read after it — `setSessions`' callback
      // runs synchronously here, so the result is available in time.
      let wroteSessions = false;
      const session: ChatSession = {
        id: newSessionId(),
        title: opts.isPrivate ? 'Private Chat' : generateTitle(messages),
        messages,
        createdAt: now,
        updatedAt: now,
        memoryEnabled: opts.isPrivate ? false : opts.memoryEnabled,
        isPrivate: opts.isPrivate,
      };

      setSessions((prev) => {
        const newSessions = [session, ...prev];
        wroteSessions = persistSessions(newSessions, session.id);
        return newSessions;
      });

      setActiveSessionId(session.id);
      // A private session's active id must not be written to storage — and
      // neither should one whose session list failed to persist, or storage
      // ends up naming an active session it does not contain. That reloads as
      // an empty thread with no explanation.
      persistActiveSession(
        opts.isPrivate || !wroteSessions ? null : session.id,
      );

      return session;
    },
    [persistSessions, persistActiveSession]
  );

  // Patch the active (or given) session's brain flags. Used by the settings toggle
  // and by "turn private mode off" (which clears isPrivate → the thread becomes
  // normal and starts persisting, keeping its existing messages).
  const setSessionFlags = useCallback(
    (sessionId: string, flags: { memoryEnabled?: boolean; isPrivate?: boolean; systemPrompt?: string }) => {
      setSessions((prev) => {
        const newSessions = prev.map((s) =>
          s.id === sessionId ? { ...s, ...flags, updatedAt: Date.now() } : s,
        );
        persistSessions(newSessions);
        return newSessions;
      });
    },
    [persistSessions],
  );

  // Update an existing session
  const updateSession = useCallback(
    (sessionId: string, messages: Message[]) => {
      setSessions((prev) => {
        const newSessions = prev.map((s) => {
          if (s.id === sessionId) {
            return {
              ...s,
              messages,
              title: s.title === 'New Chat' ? generateTitle(messages) : s.title,
              updatedAt: Date.now(),
            };
          }
          return s;
        });
        persistSessions(newSessions);
        return newSessions;
      });
    },
    [persistSessions]
  );

  // Rename a session
  const renameSession = useCallback(
    (sessionId: string, title: string) => {
      setSessions((prev) => {
        const newSessions = prev.map((s) => {
          if (s.id === sessionId) {
            return { ...s, title, updatedAt: Date.now() };
          }
          return s;
        });
        persistSessions(newSessions);
        return newSessions;
      });
    },
    [persistSessions]
  );

  // Delete a session
  const deleteSession = useCallback(
    (sessionId: string) => {
      setActiveSessionId((currentActiveId) => {
        if (currentActiveId === sessionId) {
          persistActiveSession(null);
          return null;
        }
        return currentActiveId;
      });

      let wasPrivate = false;
      setSessions((prev) => {
        wasPrivate = prev.some((s) => s.id === sessionId && s.isPrivate);
        const newSessions = prev.filter((s) => s.id !== sessionId);
        // The one legitimate route to an empty list: the user deleted their
        // last session and means it.
        persistSessions(newSessions, undefined, { allowEmpty: true });
        return newSessions;
      });

      // Also delete from remote — skip for private sessions (never saved there).
      if (authenticated && !wasPrivate) {
        deleteRemoteSession(sessionId).catch((err) =>
          console.error('Failed to delete remote session:', err)
        );
      }
    },
    [persistSessions, persistActiveSession, authenticated]
  );

  // Switch to a session. Don't persist the active id if the target is private.
  const switchSession = useCallback(
    (sessionId: string) => {
      setActiveSessionId(sessionId);
      setSessions((prev) => {
        const target = prev.find((s) => s.id === sessionId);
        persistActiveSession(target?.isPrivate ? null : sessionId);
        return prev;
      });
    },
    [persistActiveSession]
  );

  // Get the active session
  const getActiveSession = useCallback((): ChatSession | null => {
    if (!activeSessionId) return null;
    return sessions.find((s) => s.id === activeSessionId) || null;
  }, [sessions, activeSessionId]);

  // Start a new chat. Pass { isPrivate } for an incognito thread, or
  // { memoryEnabled } to seed the brain flag of a normal thread.
  const startNewChat = useCallback(
    (opts: { isPrivate?: boolean; memoryEnabled?: boolean } = {}) => {
      return createSession([], opts);
    },
    [createSession]
  );

  return {
    sessions,
    activeSessionId,
    isHydrated,
    /**
     * Whether the session list is SETTLED — local hydration done, and the
     * remote merge finished or not applicable (signed out, or sync disabled).
     *
     * `isHydrated` only covers localStorage. A signed-in user's conversations
     * arrive later from the remote index, so anything that concludes "this
     * conversation does not exist" must wait for this instead — otherwise it
     * fires for every deep link opened before the merge lands.
     */
    isSettled: isHydrated && (!enabled || !authenticated || remoteSynced),
    createSession,
    updateSession,
    renameSession,
    deleteSession,
    switchSession,
    getActiveSession,
    setSessionFlags,
    startNewChat,
  };
}
